import {
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  Typography,
  Button,
} from '@mui/material'
import { Field } from 'components/DocumentDetail/types'
import { useRootLoaderData } from 'components/Root.loader'
import FullHeightFlexContainer from 'components/ui/FullHeightFlexContainer'
import { DocumentInvoiceV2, InvoiceItemLine } from 'entities/DocumentsInvoiceV2'
import getInvoiceFields from 'entities/getInvoiceFields'
import { useMemo } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'

import { ITEM_TEMPLATE, ITEM_KEYS } from './common'
import ItemRow from './ItemRow'

interface Props {
  docDetail: DocumentInvoiceV2
}
type FieldWithEditId = InvoiceItemLine & { editId: string }

function Items({ docDetail }: Props) {
  const { config } = docDetail

  const visibleItems = ITEM_KEYS.filter((key) => config.itemLines[key]?.display || false)

  const isChecking = docDetail.isChecking
  const rootData = useRootLoaderData()
  const userGroupConfig = rootData.userGroupConfig
  const currentFields = useMemo(
    // TODO: fix docDetail type
    () => getInvoiceFields(docDetail, userGroupConfig, rootData.expenseTypesOptions),
    [docDetail, userGroupConfig, rootData],
  )

  const { control } = useFormContext()
  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'itemLines',
    keyName: 'editId',
  })

  const getFieldsConfig = (id: string) => {
    // item line can be added which can't have field data
    // line[0] is id
    const rtn = currentFields?.itemLines?.find((line) => line[0] === id)
    return rtn ? rtn.slice(1) : currentFields?.itemDefaultField
  }

  const handleAdd = (idx) => {
    insert(idx, { ...ITEM_TEMPLATE, order: 0 })
  }

  const labelCells = visibleItems.map((key) => {
    if (
      config?.itemLines[key]?.display === false ||
      (isChecking && config.itemLines?.[key]?.verify === false)
    ) {
      return null
    }
    return (
      <TableCell key={key} sx={{ whiteSpace: 'noWrap' }}>
        {docDetail.getLabel('itemLines', key)}
        {key === 'itemName' && (
          <Typography display="inline" color="error">
            {' '}
            *
          </Typography>
        )}
      </TableCell>
    )
  })

  const empty = (
    <>
      <TableCell />
      <TableCell colSpan={visibleItems.length}>
        <Button
          disabled={!docDetail.canEdit()}
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => handleAdd(0)}
        >
          品目を追加
        </Button>
      </TableCell>
    </>
  )

  const rows = (fields as FieldWithEditId[])?.map((line, idx) => (
    <ItemRow
      key={line.editId}
      editId={line.editId}
      editIdx={idx}
      defaultValues={line}
      fieldsConfig={getFieldsConfig(line.id) as Field[]}
      id={line.id}
      onDel={() => remove(idx)}
      onAdd={() => handleAdd(idx + 1)}
      canEdit={docDetail.canEdit()}
      isChecking={isChecking}
      config={config}
    />
  ))

  return (
    <FullHeightFlexContainer>
      <TableContainer style={{ height: '100%' }}>
        <Table stickyHeader size="medium" aria-label="item fields table">
          <TableHead data-testid="table-head-items-edit">
            <TableRow>
              <TableCell />
              {labelCells}
            </TableRow>
          </TableHead>
          <TableBody data-testid="table-body-items-edit">{rows.length ? rows : empty}</TableBody>
        </Table>
      </TableContainer>
    </FullHeightFlexContainer>
  )
}

export default Items
